import {Component, inject, Input, OnInit} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {FileWidget, Tabs} from '@core/components';
import {Attachment, GroupCourse, GroupItem, GroupNote, GroupPolicy} from '@core/types';
import {AttachmentService, GroupService} from '@core/services';
import {CompanyStateService} from '@core/states';

@Component({
  selector: 'core-modal-group-file',
  imports: [
    Tabs,
    FileWidget,
    FormsModule
  ],
  standalone: true,
  templateUrl: './group-file.html',
  styleUrl: './group-file.scss',
  host: {
    style: 'width: 100%;display: flex;flex-direction: column;gap: 1.25rem;padding: 0 1.5rem 1.5rem'
  }
})
export class GroupFile implements OnInit {
  @Input() group: GroupItem | null = null;

  readonly tabSkeletonItems = [1, 2, 3];
  courses: GroupCourse[] = [];
  policies: GroupPolicy[] = [];
  notes: GroupNote[] = [];
  attachments: Attachment[] = [];
  isLoadingCourses = false;
  isLoadingPolicies = false;
  isLoadingNotes = false;
  isLoadingAttachments = false;
  isUploadingAttachment = false;
  isSubmittingNote = false;
  deletingNoteIds: Array<string | number> = [];
  pendingAttachmentSkeletons = 0;
  isRefreshingNotes = false;
  coursesError = '';
  policiesError = '';
  notesError = '';
  attachmentsError = '';
  noteDraft = '';
  private readonly groupService = inject(GroupService);
  private readonly attachmentService = inject(AttachmentService);
  private readonly companyState = inject(CompanyStateService);

  ngOnInit() {
    if (!this.group?.id) return;
    this.loadCourses();
    this.loadPolicies();
    this.loadNotes();
    this.loadAttachments();
  }

  get companyId() {
    const companyId = this.companyState.membership()?.company?.id;
    return companyId ? String(companyId) : '';
  }

  get employeesCount() {
    return this.group?.employees?.length || 0;
  }

  get managerTag() {
    return this.group?.manager?.name || '';
  }

  get pendingAttachmentPlaceholders() {
    return Array.from({length: this.pendingAttachmentSkeletons}, (_, index) => index);
  }

  isDeletingNote(noteId: string | number) {
    return this.deletingNoteIds.some(id => String(id) === String(noteId));
  }

  authorName(note: GroupNote) {
    return note.author?.name || 'Sin autor';
  }

  formatFileSize(size?: number): string {
    if (!size) return '0 KB';
    if (size < 1024) return `${size} B`;
    const kb = size / 1024;
    if (kb < 1024) return `${kb.toFixed(2)} KB`;
    return `${(kb / 1024).toFixed(2)} MB`;
  }

  formatDate(value?: string) {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString('es-ES');
  }

  async submitNote() {
    if (!this.group?.id || this.isSubmittingNote) return;
    const content = this.noteDraft.trim();
    if (!content) {
      this.notesError = 'Escribe una nota antes de guardar.';
      return;
    }

    this.isSubmittingNote = true;
    this.notesError = '';
    this.isRefreshingNotes = true;
    const response = await this.groupService.addGroupNote(this.group.id, content, {skipLoader: true});

    if (!response.success || !response.result) {
      this.isSubmittingNote = false;
      this.isRefreshingNotes = false;
      this.notesError = response.message || 'No se pudo agregar la nota.';
      return;
    }

    this.noteDraft = '';
    await this.loadNotes();
    this.isSubmittingNote = false;
    this.isRefreshingNotes = false;
  }

  async deleteNote(noteId: string | number) {
    if (this.isDeletingNote(noteId)) return;

    this.deletingNoteIds = [...this.deletingNoteIds, noteId];
    const response = await this.groupService.deleteGroupNote(noteId, {skipLoader: true});
    this.deletingNoteIds = this.deletingNoteIds.filter(id => String(id) !== String(noteId));

    if (!response.success) {
      this.notesError = response.message || 'No se pudo eliminar la nota.';
      return;
    }

    this.notes = this.notes.filter(note => String(note.id) !== String(noteId));
  }

  async handleAttachmentSelection(event: Event) {
    if (!this.group?.id || this.isUploadingAttachment) return;

    const input = event.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    input.value = '';
    if (!files.length) return;

    this.isUploadingAttachment = true;
    this.pendingAttachmentSkeletons = files.length;
    this.attachmentsError = '';
    const response = await this.attachmentService.uploadAttachments({
      companyId: this.companyId,
      files,
      relatedToType: 'App\\Entity\\Core\\Group',
      relatedToId: this.group.id,
      skipLoader: true
    });
    this.isUploadingAttachment = false;
    this.pendingAttachmentSkeletons = 0;

    if (!response.success) {
      this.attachmentsError = response.message || 'No se pudieron subir los adjuntos.';
      return;
    }

    await this.loadAttachments();
  }

  async downloadAttachment(attachment: Attachment) {
    if (!attachment?.id) return;
    const response = await this.attachmentService.downloadAttachment(attachment.id, undefined, {skipLoader: true});
    if (!response.success || !response.result) {
      this.attachmentsError = response.message || 'No se pudo descargar el adjunto.';
      return;
    }

    const url = URL.createObjectURL(response.result);
    const link = document.createElement('a');
    link.href = url;
    link.download = attachment.fileName || 'adjunto';
    link.click();
    URL.revokeObjectURL(url);
  }

  private async loadCourses() {
    if (!this.group?.id) return;
    this.isLoadingCourses = true;
    this.coursesError = '';
    const response = await this.groupService.getGroupCourses(this.group.id, {skipLoader: true});
    this.isLoadingCourses = false;

    if (!response.success || !response.result) {
      this.coursesError = response.message || 'No se pudieron cargar los cursos.';
      return;
    }

    this.courses = response.result;
  }

  private async loadPolicies() {
    if (!this.group?.id) return;
    this.isLoadingPolicies = true;
    this.policiesError = '';
    const response = await this.groupService.getGroupPolicies(this.group.id, {skipLoader: true});
    this.isLoadingPolicies = false;

    if (!response.success || !response.result) {
      this.policiesError = response.message || 'No se pudieron cargar las políticas.';
      return;
    }

    this.policies = response.result;
  }

  private async loadNotes() {
    if (!this.group?.id) return;
    this.isLoadingNotes = true;
    this.notesError = '';
    const response = await this.groupService.getGroupNotes(this.group.id, {skipLoader: true});
    this.isLoadingNotes = false;

    if (!response.success || !response.result) {
      this.notesError = response.message || 'No se pudieron cargar las notas.';
      return;
    }

    this.notes = response.result;
  }

  private async loadAttachments() {
    if (!this.group?.id) return;
    this.isLoadingAttachments = true;
    this.attachmentsError = '';
    const response = await this.groupService.getGroupAttachments(this.group.id, {skipLoader: true});
    this.isLoadingAttachments = false;

    if (!response.success || !response.result) {
      this.attachmentsError = response.message || 'No se pudieron cargar los adjuntos.';
      return;
    }

    this.attachments = response.result;
  }
}
